import * as logger from "../utils/logger";
import { handleApi } from "../utils/error";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import { getCurrentVehicle, isNoVehicle } from "../utils/device";
import DriverChangeLog, { IDriverChange } from "../model/DriverChangeLog";
import DriverChangeLogs from "../model/DriverChangeLogs";
import { TSyncCallback } from "./common";
import { getServerDate } from "../utils/storage/date";
import { IApiError, TMultiCallParam } from "../utils/api.interfaces";
import BaseSync, { ISyncOptions } from "./base";
import { isOverLimitException } from "../utils/errorHandler/error.types";
import { isDeviceAccessException } from "../utils/errorHandler/error.messages";
import { getDriverChange, getDriverChangeParams } from "./driverChangeHelper";
import { MeasureName } from "../utils/performanceProfiler/measureName";
import PerformanceProfileBatcher from "../utils/performanceProfiler/performanceProfileBatcher";
import { noteDriverChangeSyncDuringLogout, noteLogoutRace } from "../utils/logoutRaceDiagnostics";

export default class DriverChangeSync extends BaseSync {

    driverChangeLogs: DriverChangeLogs;
    private firstCall = true;

    constructor(options: ISyncOptions) {
        super(options);
        this.driverChangeLogs = new DriverChangeLogs(this.api, options.store);
    }

    defaults() {
        return {
            name: "Sync: Driver Changes"
        };
    }

    addItem (model: DriverChangeLog) {
        this.driverChangeLogs.addLogs([model]);
    }

    getLastDeviceDriverChange(device?: IIdEntity) {
        return new Promise<DriverChangeLog | null>((resolve, reject) => {
            if (device && device.id) {
                getDriverChange(this.api, getServerDate().toISOString(), device.id)
                    .then((result: IDriverChange[]) => {
                        if (result.length) {
                            resolve(new DriverChangeLog(result[0]));
                        } else {
                            resolve(null);
                        }
                    }, reject);
            } else {
                resolve(null);
            }
        });
    }

    getLastDriverChange(device?: IIdEntity, force = false) {
        return new Promise<DriverChangeLog | null>((resolve, reject) => {
            let currentDriver = this.userService.getDriversSeatUser();
            if (!force && !this.firstCall && currentDriver?.id !== this.currentUser?.id) {
                resolve(null);
                return;
            }

            if (currentDriver?.id && device?.id) {
                let searchDateString = getServerDate().toISOString();
                let calls: TMultiCallParam[] = [
                    ["Get", getDriverChangeParams(searchDateString, device.id)],
                    ["Get", getDriverChangeParams(searchDateString, undefined, currentDriver.id)]
                ];
                this.api.multiCall(calls, ([deviceDriverChanges, userDriverChanges]) => {
                    let deviceDriverChange: DriverChangeLog | null = deviceDriverChanges.length ? new DriverChangeLog(deviceDriverChanges[0]) : null;
                    let userDriverChange: DriverChangeLog | null = userDriverChanges.length ? new DriverChangeLog(userDriverChanges[0]) : null;
                    if (!userDriverChange || !deviceDriverChange) {
                        resolve(deviceDriverChange || userDriverChange);
                        return;
                    }

                    if (deviceDriverChange?.id !== userDriverChange?.id) {
                        // A codriver is assigned to the vehicle. Prioritize staying with the current vehicle over checking if the previous driver switched vehicles
                        if (deviceDriverChange.driver.id !== "UnknownDriverId"
                            && deviceDriverChange.driver.id !== currentDriver?.id
                            && this.getLoggedInDriverFromDriverChange(deviceDriverChange.driver)) {
                            resolve(deviceDriverChange);
                            return;
                        }

                        if (userDriverChange.device.id && userDriverChange.device.id !== device.id) {
                            getDriverChange(this.api, searchDateString, userDriverChange.device.id)
                                .then((driverChanges: IDriverChange[]) => {
                                    resolve(driverChanges.length ? new DriverChangeLog(driverChanges[0]) : deviceDriverChange);
                                }, reject);
                            return;
                        }
                    }
                    resolve(deviceDriverChange);
                }, reject);
            } else {
                resolve(null);
            }
        });
    }

    /**
     * Validation of currently selected vehicle. Other users may have taken the device, or perhaps
     * drivers have already selected another vehicle in another app.
     * @param driverChange The last driver change fetched by the server. If null, use the locally stored driver changes instead.
     */
    checkIfCurrentDriver (driverChange: DriverChangeLog | null) {
        let driver = this.userService.getDriversSeatUser();
        if (!driver || !(driver.hasOwnProperty("keys") || driver.hasOwnProperty("driverGroups"))) {
            return;
        }

        const driverChangeSource = driverChange ? "server" : "local";
        let lastDriverChange = driverChange;
        if (!lastDriverChange) {
            let storedDriverChanges = this.driverChangeLogs.getLastDriverChangeForAllDrivers(this.userService.getUsers(true).map(user => user.id));
            lastDriverChange = storedDriverChanges.length ? storedDriverChanges[storedDriverChanges.length - 1] : null;
        }

        let currentVehicle = getCurrentVehicle();
        if (currentVehicle && !isNoVehicle() && lastDriverChange) {
            let currentDriver = this.userService.getDriversSeatUser();
            let lastDriver = lastDriverChange.driver;
            let lastVehicle = lastDriverChange.device;
            let isLastDriverUnknown = lastDriver.id === "UnknownDriverId";
            let loggedInDriver: DeepReadonlyObject<IUser> | null = null;
            let hasSwitchedVehicles = lastVehicle.id !== currentVehicle.id;

            if (!isLastDriverUnknown) {
               loggedInDriver = this.getLoggedInDriverFromDriverChange(lastDriver);
            }

            if (loggedInDriver && loggedInDriver.id !== currentDriver?.id) {
                // Driver's seat driver has changed to one of the codrivers. Update the driver's seat
                this.userService.setDriversSeatUser(loggedInDriver.name, false, false, true);
            }

            const diagnosticDriver = loggedInDriver || currentDriver;

            // MYG-147181 (logging only): record VehicleRemoved being dispatched by a DriverChange sync while logout is in progress.
            const logLogoutRace = (branch: string) => noteLogoutRace("driverChangeVehicleRemoved", () => ({
                branch,
                lastDriverId: lastDriver.id,
                lastDeviceId: lastVehicle.id,
                currentVehicleId: currentVehicle?.id,
                driverChangeDateTime: lastDriverChange?.dateTime
            }));

            if (hasSwitchedVehicles && loggedInDriver) {
                // Vehicle is changed but the driver is still in the app. Show new association prompt
                logLogoutRace("assignedNewVehicle");
                logger.log(logger.Source.USER_EVENT, `[VehicleDissociation] ${ JSON.stringify({
                    branch: "assignedNewVehicle",
                    source: driverChangeSource,
                    currentDriverId: currentDriver?.id,
                    currentVehicleId: currentVehicle.id,
                    driverChangeId: lastDriverChange.id,
                    driverChangeDriverId: lastDriver.id,
                    driverChangeDeviceId: lastVehicle.id,
                    driverChangeDateTime: lastDriverChange.dateTime,
                    targetVehicleId: lastVehicle.id
                }) }`, diagnosticDriver);
                dispatcher.trigger(DispatcherEvents.VehicleRemoved, lastVehicle, new Date(lastDriverChange.dateTime));
            } else if (!loggedInDriver) {
                // Driver not assigned to vehicle. Show dissociation prompt
                logLogoutRace("dissociated");
                logger.log(logger.Source.USER_EVENT, `[VehicleDissociation] ${ JSON.stringify({
                    branch: "dissociated",
                    source: driverChangeSource,
                    currentDriverId: currentDriver?.id,
                    currentVehicleId: currentVehicle.id,
                    driverChangeId: lastDriverChange.id,
                    driverChangeDriverId: lastDriver.id,
                    driverChangeDeviceId: lastVehicle.id,
                    driverChangeDateTime: lastDriverChange.dateTime,
                    targetVehicleId: "NoDeviceId"
                }) }`, diagnosticDriver);
                dispatcher.trigger(DispatcherEvents.VehicleRemoved, undefined, new Date(lastDriverChange.dateTime));
            }
        }
    }

    synchronize(progressCb: TSyncCallback, force = false, profilingBatcher?: PerformanceProfileBatcher): Promise {
        return this.createProfiledSync(MeasureName.SyncDriverChange, () => {
            return new Promise(resolve => {
                this.fetchData((errorObject?) => {
                    progressCb("DriverChange", errorObject);
                    resolve();
                }, force);
            });
        }, profilingBatcher);
    }

    private pushLogs (newLogs: DriverChangeLog[]) {
        return new Promise((resolve, reject) => {
            if (newLogs.length) {
                let newLogsWithDevice = newLogs.filter(log => log.device.id !== "NoDeviceId");

                let calls = newLogsWithDevice.filter(log => log._syncCounter > 0).map(log => {
                    log._syncCounter--;
                    let driver = log.driver;
                    let driverUser = driver.id === "UnknownDriverId" ? this.currentUser : this.userService.getUserById(driver.id);
                    let driverContext = driverUser && this.getCachedContext(driverUser.name);
                    let driverApiInstance = driverContext ? driverContext.api : this.api;

                    if (!driverContext) {
                        logger.log(logger.Source.SYNC, `Can't get driver (id: ${ driver.id }) context to sync DriverChange. Use current api user.`, this.currentUser);
                    }

                    return new Promise((res, rej) => {
                        driverApiInstance.call("Add", {
                            typeName: "DriverChange",
                            entity: log
                        }, res, rej);
                    });
                });

                Promise.all(calls).then(ids => {
                    // Remove all null ids. null id is now {}
                    let nonNullIds = ids.filter(id => !!id && typeof id === "string");

                    logger.event(
                        logger.Event.ADDDRIVERCHANGE,
                        `Successfully uploaded ${ calls.length } driver changes. Non-null IDs: ${ nonNullIds.join(", ") }`,
                        this.currentUser
                    );

                    if (nonNullIds.length > 0) {
                        this.api.multiCall(nonNullIds.map(id => ["Get",
                        {
                            typeName: "DriverChange",
                            search: { id }
                        }] as TMultiCallParam), logs => {
                            this.driverChangeLogs.addLogs([].concat.apply([], logs).map(log => new DriverChangeLog(log)));
                            resolve();
                        }, () => {
                            resolve();
                        });
                    } else {
                        resolve();
                    }
                }, (serverException: IApiError) => {
                    logger.error(
                        logger.Source.SYNCERROR,
                        `Failed to upload ${ calls.length } driver changes. Error: ${ serverException?.message || "Unknown error" }`,
                        this.currentUser
                    );

                    if (isOverLimitException(serverException)) {
                        reject(serverException);
                        return;
                    }
                    let requestIndex = serverException.data.requestIndex;
                    let failedLog: DriverChangeLog = newLogsWithDevice[requestIndex];

                    if (isDeviceAccessException(serverException)) {
                        // Remove all unsaved logs with inaccessible device
                        handleApi(serverException, true);
                        let currentLogs = this.driverChangeLogs.getLogs();
                        this.driverChangeLogs.setLogs(currentLogs.filter(log => log.device.id !== failedLog.device.id && !log.id));
                        void this.pushLogs(this.driverChangeLogs.getNewLogs()).then(resolve).catch(reject);
                    } else {
                        reject(serverException);
                    }
                });
            } else {
                resolve();
            }
        });
    }

    private getLoggedInDriverFromDriverChange(driverChangeDriver: IIdEntity) {
        let allUsers = this.userService.getUsers(true);
        for (let i = 0; i < allUsers.length; i++) {
            if (driverChangeDriver.id === allUsers[i].id) {
                return allUsers[i];
            }
        }
        return null;
    }

    private getServerLogs(force: boolean): Promise<DriverChangeLog | null> {
        let currentVehicle = getCurrentVehicle();
        return !currentVehicle || currentVehicle.id === "NoDeviceId"
            ? Promise.resolve(null)
            : this.getLastDriverChange(currentVehicle, force);
    }

    private fetchData (postSyncCallback, force: boolean): void {
        this.currentUser = this.api.getUser();

        if (this.currentUser?.id) {
            let newLogs = this.driverChangeLogs.getNewLogs();
            this.pushLogs(newLogs)
                .then(() => this.getServerLogs(force))
                .then((lastDriverChange: DriverChangeLog | null) => {
                    if (lastDriverChange) {
                        this.driverChangeLogs.addLogs([lastDriverChange]);
                        // MYG-147181 (logging only): count checkIfCurrentDriver running mid-logout.
                        noteDriverChangeSyncDuringLogout("checkIfCurrentDriver");
                        this.checkIfCurrentDriver(lastDriverChange);
                    }
                    this.driverChangeLogs.purgeNewLogs();
                    this.firstCall = false;
                    postSyncCallback && postSyncCallback();
                    // MYG-147181 (logging only): count SyncDriverChange dispatched mid-logout and arm the logout stall watchdog.
                    noteDriverChangeSyncDuringLogout("syncDriverChangeDispatched");
                    dispatcher.trigger(DispatcherEvents.SyncDriverChange);
                })
                .catch(error => {
                    postSyncCallback && postSyncCallback(error);
                    // MYG-147181 (logging only): count SyncDriverChange dispatched mid-logout and arm the logout stall watchdog.
                    noteDriverChangeSyncDuringLogout("syncDriverChangeDispatched");
                    dispatcher.trigger(DispatcherEvents.SyncDriverChange);
                    handleApi(error);
                });
        } else {
            postSyncCallback && postSyncCallback();
        }
    }
}
